"use client";

import React from "react";
import Link from "next/link";
import { WishlistHeartButton } from "@/src/shared/features/wishlist";
import ListingCardSkeleton from "@/src/shared/components/listing/skeletons/ListingCardSkeleton";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { IRank, IListing } from "@/src/shared/types/listing"; // Import the Listing type
import {
  dateDifferenceV2,
  priceCheckingModelV2,
} from "@/src/shared/utils/paymentChecking";
import { useAppRouter } from "@/src/shared/hooks/useAppRouter";
import ReviewAverageStar from "@/src/shared/components/atoms/ReviewAverageStar";
import analytics from "@/src/lib/analytics";
import { isHotelPortfolio } from "@/src/shared/utils/propertyType";
import Image from "next/image";
import CardImage from "@/src/shared/components/listing/CardImage";
import { useImageCarousel } from "@/src/shared/hooks/useImageCarousel";
import SponsoredBadge from "@/src/shared/components/atoms/SponsoredBadge";
import BadgeLeadingIcon from "@/src/shared/components/atoms/BadgeLeadingIcon";
import { listingBadgeService } from "@/src/shared/services/listingBadge";
import { listingDetailsHref, withQuery } from "@/src/shared/utils/listingHref";
import { flyFromCard } from "@/src/shared/utils/heroFlightStore";

interface IListingCardProps {
  data: IListing;
  isMobile?: boolean;
  isGridView?: boolean; // New prop for view mode
  rank?: IRank; // New prop for recommendation tag
  /** Added to the details link, e.g. a boosted card's `boost_id` and `from_page`. */
  linkQuery?: Record<string, string>;
  /** A paid-boosted (Featured Stays) card: shows the app's "Sponsored" pill. */
  isSponsored?: boolean;
  /** A square photo as wide as the card (the app's popup cards), instead of a fixed size. */
  squarePhoto?: boolean;
}

/**
 * A rail card's photo width: two to a phone's row, three on a small tablet, then the
 * card's own 174–283px. Grid cards keep the full-width phone size.
 */
const RAIL_PHOTO_SIZES =
  "(max-width: 639px) 50vw, (max-width: 1023px) 33vw, 283px";
const ListingCard: React.FC<IListingCardProps> = ({
  data,
  isMobile: _isMobile,
  isGridView = false,
  rank,
  linkQuery,
  isSponsored = false,
  squarePhoto = false,
}) => {
  const router = useAppRouter();

  // Derive isHotel + baseImages BEFORE useState so the initial currentIndex
  // matches the actual slider length. For hotels, baseImages prepends
  // hotel_cover_photo, so `data.images.length` alone is unreliable
  // (cover + 1 image → 2 slides but data.images.length === 1).
  const isHotel = data ? isHotelPortfolio(data) : false;
  const baseImages = data
    ? isHotel
      ? [
          ...(data.hotel_cover_photo
            ? [{ id: -1, url: data.hotel_cover_photo, priority: 0 }]
            : []),
          ...(data?.images?.slice(0, 1) || []),
        ].map((x, index) => ({
          ...x,
          isDetailsButton: index === 1,
        }))
      : data?.images?.slice(0, 2).map((x, index) => ({
          ...x,
          isDetailsButton: index === 1,
        })) || []
    : [];

  const {
    slides: extendedImages,
    hasMultiple,
    currentIndex,
    currentDot,
    disableTransition,
    prev: handlePrev,
    next: handleNext,
    goTo: handleDotClick,
    onTransitionEnd,
    touchHandlers: { onTouchStart, onTouchMove, onTouchEnd },
    consumeSwipe,
  } = useImageCarousel(baseImages);

  if (!data) {
    return <ListingCardSkeleton />;
  }

  // Get dates from query parameters
  const from = router.query.from as string;
  const to = router.query.to as string;

  const priceModel = priceCheckingModelV2(data);

  // Conditional sizing based on view mode
  const imageClasses = squarePhoto
    ? "min-w-full w-full aspect-square object-cover"
    : isGridView
      ? "min-w-full w-[283px] h-[283px] object-cover"
      : "min-w-full w-[174px] h-[174px] object-cover";

  const containerClasses = isGridView
    ? "relative overflow-hidden rounded-2xl"
    : "relative overflow-hidden rounded-2xl";

  const cardContainerClasses = isGridView
    ? "cursor-pointer group relative  block"
    : "cursor-pointer group relative  block";

  const handleCardClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    consumeSwipe(e);
    // On a phone, the app's photo flight into the details page.
    flyFromCard(e, data.id, data.title);

    analytics.view_item({ item: data, params: { page: "listing_details" } });
  };

  // Format price with commas
  const formatPrice = (price: number): string => {
    return price.toLocaleString("en-BD");
  };

  const ratingText =
    (data?.reviews_count ?? 0) === 0
      ? "New"
      : Number(data?.reviews_avg ?? 0).toFixed(2);

  return (
    <Link
      href={withQuery(
        listingDetailsHref(data.id, isHotel, router.query),
        linkQuery,
      )}
      onClick={handleCardClick}
      className={cardContainerClasses}
    >
      {/* Slider container */}
      <div
        className={containerClasses}
        data-hero-id={data.id}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Save to wishlist (top-right), as on the app's cards */}
        <div className="absolute right-1.5 top-1.5 z-20">
          <WishlistHeartButton listingId={data.id} />
        </div>

        {/* Recommendation Tag */}
        {isSponsored && (
          <div className="absolute left-3 top-3 z-20">
            <SponsoredBadge />
          </div>
        )}
        {!isSponsored && rank && rank.name && (
          <div className="absolute top-3 left-3 z-20">
            <div className="bg-white rounded-full px-3 py-1 shadow-sm flex items-center justify-center gap-1">
              <BadgeLeadingIcon leading={listingBadgeService.leading(rank)} />
              <span className="text-12 font-medium text-ink-black leading-[18px]">
                {rank.name}
              </span>
            </div>
          </div>
        )}

        <div
          className="flex"
          style={{
            transform: `translateX(-${currentIndex * 100}%)`,
            transition: disableTransition
              ? "none"
              : "transform 500ms ease-in-out",
          }}
          onTransitionEnd={onTransitionEnd}
        >
          {!extendedImages.length && (
            <div className={imageClasses + " relative"}>
              <Image
                src="/images/place-holder-brand-image.png"
                alt="place"
                fill
                className="object-cover rounded-2xl"
                sizes="(max-width: 768px) 100vw, 283px"
                priority={false}
                unoptimized
              />
            </div>
          )}

          {extendedImages.map((src, i) => (
            <CardImage
              key={`slide-${i}-${src.id}`}
              src={src.url}
              alt={`${data.title + src.id}`}
              sizes={isGridView ? undefined : RAIL_PHOTO_SIZES}
              className={imageClasses}
              index={i}
              isSeeMoreButton={src.isDetailsButton}
            />
          ))}
        </div>
        {/* Arrows */}
        {hasMultiple && (
          <>
            <button
              onClick={handlePrev}
              className="absolute top-1/2 left-2 transform -translate-y-1/2 bg-white text-black rounded-full shadow-md p-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity hover:scale-105"
              aria-label="Previous image"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={handleNext}
              className="absolute top-1/2 right-2 transform -translate-y-1/2 bg-white text-black rounded-full shadow-md p-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity hover:scale-105"
              aria-label="Next image"
            >
              <ChevronRight size={16} />
            </button>
          </>
        )}

        {/* Dots */}
        {hasMultiple && (
          <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-1 z-10">
            {baseImages.map((_, index) => (
              <button
                key={index}
                onClick={(e) => handleDotClick(e, index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentDot ? "bg-white" : "bg-white/50"
                } hover:bg-white/80`}
                aria-label={`Go to image ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
      {/* Details */}
      <div className="mt-2 space-y-2 flex-1">
        {/* Title with 2-line truncation using Tailwind */}
        <h2
          className="font-semibold text-ink-black text-xs sm:text-sm overflow-hidden text-ellipsis"
          style={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
          title={data?.title}
        >
          {data?.title}
        </h2>

        {/* Price Section - Stacked Layout */}
        <div className="space-y-1">
          {/* Discount Price Row - Grid View */}
          {priceModel?.discountPrice && isGridView && (
            <div className="flex items-center justify-between">
              {/* Left: Discount Price + Per Day Price together */}
              <div className="flex items-baseline space-x-2">
                <span className="font-medium text-sm text-ink-gray line-through">
                  BDT {formatPrice(priceModel?.discountPrice)}
                </span>
                <div className="font-medium text-sm text-brand-500 flex items-baseline">
                  {isHotel ? (
                    <span className="">
                      Start From{" "}
                      {formatPrice(data.price || data.min_price || 0)} BDT
                    </span>
                  ) : (
                    <>
                      <span className="">
                        BDT{" "}
                        {priceModel?.perDayPrice
                          ? formatPrice(priceModel?.perDayPrice)
                          : 0}
                      </span>
                      <span className="ml-[1px]"> per day</span>
                    </>
                  )}
                </div>
              </div>
              {/* Right: Rating */}
              <div className="flex items-center">
                <ReviewAverageStar rating={data?.reviews_avg || 0} />
                <span className="font-medium text-xs text-ink-gray">
                  {ratingText}
                </span>
              </div>
            </div>
          )}

          {/* Discount Price Row - List View */}
          {priceModel?.discountPrice && !isGridView && (
            <div className="flex items-center justify-between">
              <span className="font-medium text-sm text-ink-gray line-through">
                BDT {formatPrice(priceModel?.discountPrice)}
              </span>
              {/* Rating Section for list view */}
              <div className="flex items-center">
                <ReviewAverageStar rating={data?.reviews_avg || 0} />
                <span className="font-medium text-xs  text-ink-gray">
                  {ratingText}
                </span>
              </div>
            </div>
          )}

          {/* Per Day Price Row - Only show if no discount OR in list view */}
          {(!priceModel?.discountPrice || !isGridView) && (
            <div className="flex items-center justify-between gap-2">
              {/* Per Day Price: "BDT 2,199" and "per day" never break inside, so a
                  narrow card (two in a phone's row) wraps between them, not into columns. */}
              <div className="font-medium text-sm text-brand-500 flex min-w-0 flex-wrap items-baseline gap-x-1">
                {isHotel ? (
                  <span className="">
                    Start From {formatPrice(data.price || data.min_price || 0)}{" "}
                    BDT
                  </span>
                ) : (
                  <>
                    <span className="whitespace-nowrap">
                      BDT{" "}
                      {priceModel?.perDayPrice
                        ? formatPrice(priceModel?.perDayPrice)
                        : 0}
                    </span>
                    <span className="whitespace-nowrap">per day</span>
                  </>
                )}
              </div>

              {/* Rating Section - only show here if no discount price */}
              {!priceModel?.discountPrice && (
                <div className="flex shrink-0 items-center">
                  <ReviewAverageStar rating={data?.reviews_avg || 0} />
                  <span className="font-medium text-xs  text-ink-gray">
                    {ratingText}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Total Price (only if dates exist in query) */}
        {from && to && (
          <div className="mt-1">
            <div className="flex items-baseline justify-between font-medium text-xs text-ink-gray">
              <span className="">
                BDT{" "}
                {priceModel?.totalPrice
                  ? formatPrice(
                      priceModel?.totalPrice * dateDifferenceV2(from, to),
                    )
                  : 0}
                /
                <small className="text-xs">
                  {dateDifferenceV2(from, to)} days
                </small>
              </span>
            </div>
          </div>
        )}
      </div>
    </Link>
  );
};

export default ListingCard;
